import {StyleSheet, css} from 'aphrodite';
import classNames from 'classnames';
import React, {memo, useCallback} from 'react';

import {
  DisplayPlateMapWell,
  MeasurementLayerById,
  PlateMapWellDisplayLayer,
  TestOrderInfoById,
} from 'samples/plate-maps/PlateMapConstants';
import PlateMapWellPopoverContent from 'samples/plate-maps/PlateMapWellPopoverContent';
import WellRenderer, {
  MulticolorStripeWellRenderer,
  MulticolorStripedOrErrorWellRenderer,
  WellRendererProps,
  WellRoleWellRenderer,
} from 'samples/plate-maps/WellRenderer';
import {GRAY_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';

const WELL_RENDERER_BY_DISPLAY_LAYER = {
  [PlateMapWellDisplayLayer.WELL_ROLE]: memo(WellRoleWellRenderer),
  [PlateMapWellDisplayLayer.REAGENT]: memo(MulticolorStripedOrErrorWellRenderer),
  [PlateMapWellDisplayLayer.SCHEMA_FIELD_TEXT]: memo(MulticolorStripedOrErrorWellRenderer),
  [PlateMapWellDisplayLayer.SCHEMA_FIELD_SELECTOR]: memo(MulticolorStripedOrErrorWellRenderer),
  [PlateMapWellDisplayLayer.SCHEMA_FIELD_NUMBER]: memo(MulticolorStripedOrErrorWellRenderer),
  [PlateMapWellDisplayLayer.TEST_DILUTION_FACTOR]: memo(MulticolorStripeWellRenderer),
  [PlateMapWellDisplayLayer.CONCENTRATION]: memo(MulticolorStripedOrErrorWellRenderer),
  [PlateMapWellDisplayLayer.MEASUREMENT]: memo(MulticolorStripedOrErrorWellRenderer),
  [PlateMapWellDisplayLayer.WELL_VOLUME]: memo(MulticolorStripedOrErrorWellRenderer),
};

export interface PlateMapWellViewProps {
  cellSize: number;
  wellSize: number;
  isHovered: boolean;
  isSelected: boolean;
  isPreview: boolean;
  well: DisplayPlateMapWell;
  displayLayer: PlateMapWellDisplayLayer;
  enablePopovers: boolean;
  dataParameterNamesByVersionId: {[schemaFieldId: string]: string};
  testOrderDilutionInfoById: TestOrderInfoById;
  concentrationLayerInfoById?: MeasurementLayerById;
  showFinalMeasurements: boolean;
}

/**
 * The meat of the plate map well view that is memoized to prevent unecessary renders.
 */
const PlateMapWellView = memo(
  ({
    cellSize,
    wellSize,
    isHovered,
    isSelected,
    isPreview,
    well,
    displayLayer,
    enablePopovers,
    dataParameterNamesByVersionId,
    showFinalMeasurements,
    testOrderDilutionInfoById,
    concentrationLayerInfoById,
  }: PlateMapWellViewProps) => {
    const {row, column} = well.position;
    const wellRendererProps = {
      cx: cellSize * 0.5,
      cy: cellSize * 0.5,
      wellSize,
      isHovered,
      isSelected,
      isPreview,
      well,
    };
    const RendererForDisplayLayer = WELL_RENDERER_BY_DISPLAY_LAYER[displayLayer];

    const renderWell = useCallback(
      (props: WellRendererProps) => <RendererForDisplayLayer {...props} />,
      [RendererForDisplayLayer]
    );

    return (
      <foreignObject x={cellSize * column} y={cellSize * row} width={cellSize} height={cellSize}>
        <Popover
          renderContent={() => (
            <PlateMapWellPopoverContent
              well={well}
              showFinalMeasurements={showFinalMeasurements}
              dataParameterNamesByVersionId={dataParameterNamesByVersionId}
              testOrderDilutionInfoById={testOrderDilutionInfoById}
              concentrationLayerInfoById={concentrationLayerInfoById}
            />
          )}
          interaction={FloatingInteraction.HOVER}
          restMs={1000}
          isDisabled={!enablePopovers}
        >
          {/* TODO(BNCH-75467): Remove the duplicated styles which are used in temporary-aphrodite-styles.less */}
          <svg
            className={classNames(css(styles.wellSvg), 'notebook-export--plate-map-well-svg')}
            width={cellSize}
            height={cellSize}
          >
            <rect width={cellSize} height={cellSize} fill={isHovered ? GRAY_LIGHT : GRAY_LIGHTER} />
            <WellRenderer {...wellRendererProps} renderWell={renderWell} />
          </svg>
        </Popover>
      </foreignObject>
    );
  }
);

export default PlateMapWellView;

const styles = StyleSheet.create({
  wellSvg: {overflow: 'visible'},
});
